import React, { useState, useEffect } from 'react';
import {
  FaShoppingCart, 
  FaDollarSign, 
  FaUsers, 
  FaUtensils, 
  FaSync, 
  FaDownload, 
  FaPlus, 
  FaChartBar, 
  FaCog, 
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaTruck,
  FaCalendarAlt,
  FaChartLine,
  FaCreditCard
} from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { adminApiService } from '../services/adminApi';
import analyticsApiService from '../services/analyticsApi';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    today_orders_count: 0,
    today_orders_diff: 0,
    today_revenue: 0,
    today_revenue_diff: 0,
    average_check: 0,
    average_check_diff: 0,
    active_dishes_count: 0,
    week_orders_count: 0,
    month_orders_count: 0,
    week_revenue: 0,
    month_revenue: 0,
    total_customers: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [popularDishes, setPopularDishes] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [paymentMethodStats, setPaymentMethodStats] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await adminApiService.getDashboardStats();
      
      setStats(response.stats || {});
      setRecentOrders(response.recent_orders || []);
      setPopularDishes(response.popular_dishes || []);
      setOrderStatuses(response.order_statuses || []);

      // Загружаем статистику способов оплаты за сегодня
      try {
        const paymentStats = await analyticsApiService.getPaymentMethodStats({ period: 'today' });
        setPaymentMethodStats(paymentStats || []);
      } catch (paymentError) {
        console.error('Ошибка загрузки статистики оплаты:', paymentError);
        setPaymentMethodStats([]);
      }

    } catch (error) {
      console.error('Ошибка загрузки данных дашборда:', error);
      setError('Ошибка загрузки данных. Проверьте подключение к серверу.');
    } finally {
      setLoading(false);
    }
  };

  // Функции форматирования
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('ru-RU').format(Math.round(num));
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '₽0';
    return `₽${formatNumber(num)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Компонент статистической карточки
  const StatCard = ({ title, value, change, icon: Icon, type = 'primary', prefix = '', suffix = '', subtitle = '' }) => {
    const getCardColors = () => {
      switch (type) {
        case 'success':
          return 'from-green-50 to-green-100 border-green-200';
        case 'warning':
          return 'from-yellow-50 to-yellow-100 border-yellow-200';
        case 'danger':
          return 'from-red-50 to-red-100 border-red-200';
        case 'info':
          return 'from-blue-50 to-blue-100 border-blue-200';
        case 'purple':
          return 'from-purple-50 to-purple-100 border-purple-200';
        default:
          return 'from-blue-50 to-blue-100 border-blue-200';
      }
    };

    const getIconColors = () => {
      switch (type) {
        case 'success':
          return 'bg-green-500';
        case 'warning':
          return 'bg-yellow-500';
        case 'danger':
          return 'bg-red-500';
        case 'info':
          return 'bg-blue-500';
        case 'purple':
          return 'bg-purple-500';
        default:
          return 'bg-blue-500';
      }
    };

    const getTextColors = () => {
      switch (type) {
        case 'success':
          return { title: 'text-green-700', value: 'text-green-900', subtitle: 'text-green-600' };
        case 'warning':
          return { title: 'text-yellow-700', value: 'text-yellow-900', subtitle: 'text-yellow-600' };
        case 'danger':
          return { title: 'text-red-700', value: 'text-red-900', subtitle: 'text-red-600' };
        case 'info':
          return { title: 'text-blue-700', value: 'text-blue-900', subtitle: 'text-blue-600' };
        case 'purple':
          return { title: 'text-purple-700', value: 'text-purple-900', subtitle: 'text-purple-600' };
        default:
          return { title: 'text-blue-700', value: 'text-blue-900', subtitle: 'text-blue-600' };
      }
    };

    const colors = getTextColors();

    return (
      <div className={`bg-gradient-to-br ${getCardColors()} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className={`text-sm font-medium ${colors.title} mb-2`}>{title}</p>
            <p className={`text-3xl font-bold ${colors.value} mb-1`}>
              {prefix}{typeof value === 'number' ? formatNumber(value) : value}{suffix}
            </p>
            {subtitle && (
              <p className={`text-xs ${colors.subtitle} mb-2`}>{subtitle}</p>
            )}
            {change !== undefined && change !== null && (
              <div className="flex items-center">
                {change > 0 ? (
                  <FaArrowUp className="w-3 h-3 text-green-500 mr-1" />
                ) : change < 0 ? (
                  <FaArrowDown className="w-3 h-3 text-red-500 mr-1" />
                ) : null}
                {change !== 0 && (
                  <>
                    <span className={`text-sm font-semibold ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(change)}%
                    </span>
                    <span className="text-sm text-gray-500 ml-1">от вчера</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={`w-14 h-14 rounded-xl ${getIconColors()} flex items-center justify-center text-white shadow-md`}>
            <Icon className="w-7 h-7" />
          </div>
        </div>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { class: 'bg-blue-100 text-blue-800', text: 'Новый', icon: FaClock },
      'confirmed': { class: 'bg-green-100 text-green-800', text: 'Подтвержден', icon: FaCheckCircle },
      'preparing': { class: 'bg-yellow-100 text-yellow-800', text: 'Готовится', icon: FaUtensils },
      'ready': { class: 'bg-purple-100 text-purple-800', text: 'Готов', icon: FaCheckCircle },
      'delivered': { class: 'bg-green-100 text-green-800', text: 'Доставлен', icon: FaTruck },
      'cancelled': { class: 'bg-red-100 text-red-800', text: 'Отменен', icon: FaTimesCircle },
    };
    
    const config = statusConfig[status] || { class: 'bg-gray-100 text-gray-800', text: 'Неизвестно', icon: FaClock };
    const StatusIcon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <StatusIcon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  // Цвета для графиков
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16'];

  // Кастомный Tooltip для графиков
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <FaTimesCircle className="w-6 h-6 text-red-500 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Ошибка загрузки данных</h3>
            <p className="text-red-700 mt-1">{error}</p>
            <button 
              onClick={loadDashboardData} 
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Заголовок */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
            <FaChartLine className="w-8 h-8 text-blue-600 mr-3" />
            Главная панель
          </h1>
          <p className="text-lg text-gray-600">Обзор ключевых показателей вашего ресторана</p>
        </div>
        <div className="flex gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            <FaDownload className="w-4 h-4" />
            Экспорт
          </button>
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            onClick={loadDashboardData}
          >
            <FaSync className="w-4 h-4" />
            Обновить
          </button>
        </div>
      </div>

      {/* Основные KPI карточки */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Заказы сегодня"
            value={stats.today_orders_count}
            change={stats.today_orders_diff}
          icon={FaShoppingCart}
          type="info"
          subtitle={`За неделю: ${formatNumber(stats.week_orders_count)}`}
          />
          <StatCard
            title="Выручка сегодня"
          value={formatCurrency(stats.today_revenue)}
            change={stats.today_revenue_diff}
          icon={FaDollarSign}
          type="success"
          subtitle={`За неделю: ${formatCurrency(stats.week_revenue)}`}
        />
          <StatCard
            title="Средний чек"
          value={formatCurrency(stats.average_check)}
            change={stats.average_check_diff}
          icon={FaChartBar}
          type="warning"
          />
          <StatCard
            title="Активные блюда"
            value={stats.active_dishes_count}
          icon={FaUtensils}
          type="purple"
          subtitle="В меню"
        />
      </div>

      {/* Способы оплаты сегодня */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Array.isArray(paymentMethodStats) && paymentMethodStats.length > 0 ? (
          paymentMethodStats.map((method, index) => {
            // Определяем тип и цвет для каждого способа оплаты
            const methodType = method.method === 'Наличными' || method.method === 'Наличные' ? 'success' : 'info';
            const methodIcon = method.method === 'Наличными' || method.method === 'Наличные' ? FaDollarSign : FaCreditCard;
            
            return (
              <StatCard
                key={index}
                title={method.method}
                value={formatCurrency(method.total_amount || 0)}
                icon={methodIcon}
                type={methodType}
                subtitle={`${formatNumber(method.count)} заказов (${method.amount_percentage || 0}%)`}
              />
            );
          })
        ) : null}
      </div>

      {/* Дополнительные метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Заказы за месяц"
          value={stats.month_orders_count}
          icon={FaCalendarAlt}
          type="info"
        />
        <StatCard
          title="Выручка за месяц"
          value={formatCurrency(stats.month_revenue)}
          icon={FaDollarSign}
          type="success"
        />
        <StatCard
          title="Всего клиентов"
          value={stats.total_customers}
          icon={FaUsers}
          type="purple"
        />
      </div>

      {/* Основной контент */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Последние заказы */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              <FaShoppingCart className="w-5 h-5 text-blue-500 mr-2" />
                Последние заказы
            </h3>
            <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors">
              <FaEye className="w-4 h-4" />
              Посмотреть все
            </button>
          </div>
          
          {recentOrders.length > 0 ? (
            <div className="space-y-4">
              {recentOrders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        <span className="text-blue-600 font-semibold">#{order.id.toString().slice(-3)}</span> - {order.customer_name}
                      </div>
                      <div className="text-sm text-gray-500">{formatDate(order.created_at)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{formatCurrency(order.total_price)}</div>
                    <div className="mt-1">{getStatusBadge(order.status)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FaShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Нет заказов</h3>
              <p className="text-gray-500">Заказы появятся здесь, когда клиенты начнут их оформлять</p>
            </div>
          )}
        </div>

        {/* Статистика статусов заказов */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <FaChartBar className="w-5 h-5 text-purple-500 mr-2" />
            Статусы заказов сегодня
          </h3>
          {orderStatuses.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={orderStatuses}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {orderStatuses.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8">
              <FaChartBar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Нет данных о статусах заказов</p>
            </div>
          )}
        </div>
      </div>

      {/* Популярные блюда */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <FaUtensils className="w-5 h-5 text-orange-500 mr-2" />
          Популярные блюда за неделю
        </h3>
        <div className="space-y-4">
          {popularDishes.length > 0 ? (
            popularDishes.map((dish, index) => (
              <div key={dish.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full text-white text-sm font-bold shadow-md ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                      index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                      'bg-gradient-to-br from-blue-400 to-blue-600'
                    }`}>
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">{dish.name}</h4>
                    <p className="text-sm text-gray-600">{formatNumber(dish.orders_count)} заказов</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-green-700">{formatCurrency(dish.total_revenue)}</p>
                  <p className="text-xs text-gray-500">выручка</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <FaUtensils className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Нет данных о популярных блюдах</p>
            </div>
          )}
        </div>
      </div>

      {/* Быстрые действия */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <FaCog className="w-5 h-5 text-gray-500 mr-2" />
          Быстрые действия
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group">
            <div className="w-14 h-14 rounded-xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-4 transition-colors">
              <FaPlus className="w-6 h-6 text-blue-600" />
            </div>
            <span className="font-semibold text-gray-700 group-hover:text-blue-700 transition-colors">Новый заказ</span>
          </button>
          
          <button className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all group">
            <div className="w-14 h-14 rounded-xl bg-green-100 group-hover:bg-green-200 flex items-center justify-center mb-4 transition-colors">
              <FaUtensils className="w-6 h-6 text-green-600" />
            </div>
            <span className="font-semibold text-gray-700 group-hover:text-green-700 transition-colors">Добавить блюдо</span>
          </button>
          
          <button className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-yellow-500 hover:bg-yellow-50 transition-all group">
            <div className="w-14 h-14 rounded-xl bg-yellow-100 group-hover:bg-yellow-200 flex items-center justify-center mb-4 transition-colors">
              <FaChartBar className="w-6 h-6 text-yellow-600" />
            </div>
            <span className="font-semibold text-gray-700 group-hover:text-yellow-700 transition-colors">Аналитика</span>
          </button>
          
          <button className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group">
            <div className="w-14 h-14 rounded-xl bg-purple-100 group-hover:bg-purple-200 flex items-center justify-center mb-4 transition-colors">
              <FaCog className="w-6 h-6 text-purple-600" />
            </div>
            <span className="font-semibold text-gray-700 group-hover:text-purple-700 transition-colors">Настройки</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 